// @flow
import * as React from 'react';
import {
  Avatar,
  ButtonBase,
  Dialog, DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import { useClient } from '../../hooks/client-context';
import {
  darkenHexColor,
  GmailMessagesByEmailQuery, letterToColorfulHex,
  lightenHexColor,
  Portal,
  useGmailMessagesByEmailQuery
} from '../../../../shared';
import moment from 'moment';
import _ from 'lodash';
import { ModalCloseButton } from '@coverright/ui/shared';
import { useState } from 'react';

type Email = GmailMessagesByEmailQuery['gmailMessagesByEmail'][0];

export const Emails = () => {
  const client = useClient();
  const [open, setOpen] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<Email>();
  const {data, loading} = useGmailMessagesByEmailQuery({
    variables: {email: client.email!},
    skip: !client.email,
    pollInterval: 60000
  })

  const emails = _.orderBy(data?.gmailMessagesByEmail || [], 'createdAt', 'desc');

  const handleClose = () => {
    setCurrentEmail(undefined);
    setOpen(false);
  }

  const handleClick = (email: Email) => {
    setCurrentEmail(email);
    setOpen(true);
  }

  return (<>
    <Dialog fullWidth maxWidth={'md'} open={open} onClose={handleClose}>
      <DialogTitle>
        {currentEmail && <Addreses email={currentEmail} />}
        <ModalCloseButton onClick={handleClose} sx={{ position: 'absolute', top: 4, right: 8 }} />
      </DialogTitle>
      <DialogContent>
        {currentEmail && <Typography dangerouslySetInnerHTML={{ __html: currentEmail.body as string }} />}
      </DialogContent>
    </Dialog>
    <Stack gap={3}>
      <Typography variant={'subtitle1'}>Email list</Typography>

      <Stack gap={2}>
        {!emails?.length && loading && <>
          <Skeleton variant={'rounded'} height={119} />
          <Skeleton variant={'rounded'} height={119} />
          <Skeleton variant={'rounded'} height={119} />
        </>}
        {emails?.map(email => (
          <ButtonBase component={Stack} gap={1} key={email.id}
                      onClick={() => handleClick(email)}
                      sx={{'&:hover': {bgcolor: 'secondary.50'}}}>
            <Portal p={'8px 16px'} gap={.5}>

              <Stack gap={1.5} direction={'row'} alignItems={'flex-start'}>
                <Addreses email={email} />

                <Typography variant={'body2'} fontSize={11} flexShrink={0} fontWeight={600} color={'primary.400'} noWrap>
                  {moment(email.createdAt).format('L H:mm:ss')}
                </Typography>
              </Stack>
              <Divider sx={{mx: -2, mb: 1}} />

              <Typography variant={'body2'} fontWeight={500}>{email.subject}</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>{email.snippet}</Typography>

            </Portal>
          </ButtonBase>
        ))}
      </Stack>
    </Stack>
  </>);
};

const Addreses = ({email}: {email: Email}) => (
  <Stack gap={1.5} direction={'row'} alignItems={'flex-start'} flex={1}>
    {email.from && <Avatar sx={{
      bgcolor: lightenHexColor(letterToColorfulHex(email.from), 60),
      color: darkenHexColor(letterToColorfulHex(email.from), 70),
      alignSelf: 'flex-start',
      width: 30, height: 30, fontWeight: 500, fontSize: 14 }}>
      {email.from[0].toUpperCase()}
    </Avatar>}
    <Stack flex={1}>
      <Typography variant={'body2'} fontWeight={500}>{email.from}</Typography>
      <Stack gap={1} direction={'row'} alignItems={'center'}>
        <Typography variant={'body2'} color={'text.secondary'}>to:</Typography>
        <Stack gap={1} direction={'row'} alignItems={'center'} sx={{border: '1px solid #d9d9d9', borderRadius: '4px', p: '2px 4px'}}>
          {email.to && <Avatar sx={{ bgcolor: lightenHexColor(letterToColorfulHex(email.to), 60), color: darkenHexColor(letterToColorfulHex(email.to), 70), width: 20, height: 20, fontWeight: 500, fontSize: 10 }}>
            {email.to[0].toUpperCase()}
          </Avatar>}
          <Typography variant={'body2'} color={'text.secondary'}>{email.to}</Typography>
        </Stack>
      </Stack>
    </Stack>
  </Stack>
)
